import { authorName } from '@components/app/AuthorBox/BaskAuthorBox'
import { buildAuthor } from '@components/app/AuthorBox/buildAuthor'
import BrickName from '@components/app/CmsBricks/brickName'
import { NavBar } from '@components/app/NavBar'
import RelatedArticleLinks from '@components/app/RelatedLinkSection/RelatedArticleLinks'
import { RelatedConditionCarousel } from '@components/app/RelatedLinkSection/RelatedConditionCarousel'
import RelatedListingLinks from '@components/app/RelatedLinkSection/RelatedListingLinks'
import RelatedTreatmentTopicLinks from '@components/app/RelatedLinkSection/RelatedTreatmentTopicLinks'
import StructuredData from '@components/app/StructuredData'
import OpenGraphCard from '@components/primitives/Layout/HeaderComponents/OpenGraphCard'
import type { PageWidth } from '@components/primitives/Layout/HeaderComponents/ResponsivePageMargin'
import TitleForHead from '@components/primitives/Layout/HeaderComponents/TitleForHead'
import { classMerge } from '@components/utilities/classMerge'
import type { GraphiteRelatedLink } from '@lib/graphiteApi'
import Routes from '@lib/routes'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import type { DirectoryForSummaryCard } from '@modules/directoryTopics/types/DirectoryForSummaryCard'
import type { TreatmentTopicForRelatedTreatmentPageProps } from '@modules/drugs/helpers/getRelatedTreatmentTopics'
import type { TrialForSummaryCard } from '@modules/trials/types/TrialForSummaryCard'
import Head from 'next/head'
import { useContext } from 'react'
import { PageViewer, ReactBricksContext, cleanPage } from 'react-bricks'
import type { types } from 'react-bricks/frontend'
import { jsonLdScriptProps } from 'react-schemaorg'
import type { Article } from 'schema-dts'

export type CmsGuidePageProps = {
  page: types.Page
  relatedArticles: GraphiteRelatedLink[]
  relatedDirectories: DirectoryForSummaryCard[]
  relatedListings: TrialForSummaryCard[]
  relatedTreatmentTopics: TreatmentTopicForRelatedTreatmentPageProps
}

function CmsGuidePage({
  page,
  relatedArticles,
  relatedDirectories,
  relatedListings,
  relatedTreatmentTopics,
}: CmsGuidePageProps) {
  const { bricks, pageTypes } = useContext(ReactBricksContext)
  const cleanedPage = cleanPage(page, pageTypes, bricks)
  const { innerColor, navBarColor, outerColor, wrapInCard } =
    page.customValues ?? {}
  // Grab first image in content as thumbnail url
  const thumbnailUrls = page.content
    .filter((c) => c.type === BrickName.PowerImage && c.props?.imageSource?.src)
    .map((c) => c.props.imageSource.src)

  const description = page.meta.description ?? ''

  return (
    <>
      <Head>
        <meta content={description ?? ''} name='description' />
      </Head>
      <div
        className={classMerge('px-4 md:px-8')}
        style={outerColor ? { backgroundColor: outerColor.color } : undefined}
      >
        <OpenGraphCard
          description={description ?? ''}
          title={page.meta.title ?? ''}
          url={absoluteUrl(DynamicRoutes.guides(page.slug))}
        />
        <StructuredData
          id={`${page.slug}-article`}
          scriptTagProps={jsonLdScriptProps<Article>({
            '@context': 'https://schema.org',
            '@type': 'Article',
            'author': buildAuthor({
              authorName,
              url: absoluteUrl(Routes.aboutMichaelGill),
            }),
            'datePublished': page.publishedAt,
            'headline': page.meta.title,
            'image': thumbnailUrls,
          })}
        />
        <TitleForHead title={page.meta.title} />
        <NavBar
          maxWidth={BLOG_CONTENT_WIDTH}
          style={
            navBarColor ? { backgroundColor: navBarColor.color } : undefined
          }
        />

        <div
          className={classMerge(
            'grid grid-cols-1 gap-4',
            BLOG_CONTENT_WIDTH,
            'mx-auto',
            wrapInCard ? 'mt-16 rounded-2xl p-8' : 'px-4 pb-12',
          )}
          style={innerColor ? { backgroundColor: innerColor.color } : undefined}
        >
          <PageViewer page={cleanedPage} />
          <RelatedListingLinks
            relatedListings={relatedListings ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
          <RelatedConditionCarousel
            relatedDirectories={relatedDirectories ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
          <RelatedArticleLinks
            relatedArticles={relatedArticles ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
          <RelatedTreatmentTopicLinks
            relatedTreatmentTopics={relatedTreatmentTopics ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
        </div>
      </div>
    </>
  )
}

const BLOG_CONTENT_WIDTH = 'max-w-4xl' as PageWidth

export default CmsGuidePage
